<template>
  <div class="tw-relative tw-px-7 tw-pb-7 tw-w-full lg:tw-w-3/4">
    <PageHeader>
      <template #header-title>
        {{ worker?.name ?? $t('Complete worker profile') }}
      </template>
      <template #header-description>
        <span v-if="worker?.name">{{ $t('Complete worker profile') }}</span>
      </template>
      <template #header-actions>
        <a :href="laravelNamedRoute('recruiter-candidates.index')" data-test="managed-worker-back-link">
          <WdsButtonGhost secondary>
            <WdsStack align-center gap-sm>
              <WdsIcon name="arrow-left" :size="16" />
              {{ $t('Candidates') }}
            </WdsStack>
          </WdsButtonGhost>
        </a>
      </template>
    </PageHeader>

    <div v-if="loading" class="tw-flex tw-items-center tw-justify-center tw-py-12">
      <WdsSpinner indeterminate />
    </div>

    <WdsAlert v-else-if="!viewerCanEdit" warning icon class="tw-mt-4">
      {{ $t('You do not have permission to edit this worker’s profile.') }}
    </WdsAlert>

    <div v-else class="tw-mt-6">
      <WdsTabs
        :active-tab-key="activeTab"
        secondary
        @update:active-tab-key="changeTab"
      >
        <WdsTab :title="$t('Profile basics')" :tab-key="MANAGED_PROFILE_TAB.PROFILE">
          <WdsStack direction-column gap-md class="tw-mt-4">
            <WdsStack direction-row gap-sm class="managed-profile-form-row">
              <div class="tw-w-full">
                <WdsInput
                  v-model="form.firstName"
                  :label="$t('First name')"
                  :placeholder="$t('First name')"
                  data-test="managed-worker-first-name"
                />
              </div>
              <div class="tw-w-full">
                <WdsInput
                  v-model="form.middleName"
                  :label="$t('Middle name')"
                  :placeholder="$t('Middle name (optional)')"
                  data-test="managed-worker-middle-name"
                />
              </div>
              <div class="tw-w-full">
                <WdsInput
                  v-model="form.lastName"
                  :label="$t('Last name')"
                  :placeholder="$t('Last name')"
                  data-test="managed-worker-last-name"
                />
              </div>
            </WdsStack>

            <WdsDivider />

            <WdsInput
              v-model="form.address.address"
              :label="$t('Street')"
              :placeholder="$t('Street name')"
              data-test="managed-worker-address-street"
            />
            <WdsStack direction-row gap-sm class="managed-profile-form-row">
              <div class="tw-w-full">
                <WdsInput
                  v-model="form.address.city"
                  :label="$t('City')"
                  :placeholder="$t('City')"
                  data-test="managed-worker-address-city"
                />
              </div>
              <div class="tw-w-full">
                <WdsInput
                  v-model="form.address.postCode"
                  :label="postCodeLabel"
                  :placeholder="postCodePlaceholder"
                  data-test="managed-worker-address-postcode"
                />
              </div>
            </WdsStack>
            <WdsStack direction-row gap-sm class="managed-profile-form-row">
              <div class="tw-w-full">
                <WdsSelect
                  v-model="selectedCountry"
                  :list="getCountries"
                  :label="$t('Country')"
                  :placeholder="$t('Select a country')"
                  searchable
                  data-test="managed-worker-address-country"
                />
              </div>
              <div v-if="countryHasStates" class="tw-w-full">
                <WdsSelect
                  v-model="selectedState"
                  :list="getStates"
                  :label="stateLabel"
                  :placeholder="statePlaceholder"
                  searchable
                  data-test="managed-worker-address-state"
                />
              </div>
            </WdsStack>

            <WdsDivider v-if="identificationApplicable" class="tw-my-4" />

            <WdsStack v-if="identificationApplicable" direction-column gap-md>
              <WdsStack direction-column gap-xs>
                <WdsHeading as="h2" type="title2">
                  {{ $t('Identification details') }}
                </WdsHeading>
                <WdsText v-if="identificationRequiredByLabel" secondary sm>
                  {{ $t('Required by :clients', { clients: identificationRequiredByLabel }) }}
                </WdsText>
              </WdsStack>

              <WdsStack direction-row gap-sm class="managed-profile-form-row">
                <div class="tw-w-full">
                  <WdsInput
                    v-model="identityForm.dateOfBirth"
                    type="date"
                    :label="$t('Date of birth')"
                    :max="yesterdayIso"
                    data-test="managed-worker-identity-date-of-birth"
                  />
                </div>
                <div class="tw-w-full">
                  <!-- spacer to keep the row at half-width -->
                </div>
              </WdsStack>

              <WdsStack direction-row gap-sm class="managed-profile-form-row">
                <div class="tw-w-full">
                  <WdsSelect
                    v-model="selectedNationality"
                    :list="getCountries"
                    :label="$t('Nationality')"
                    :placeholder="$t('Select a country')"
                    searchable
                    data-test="managed-worker-identity-nationality"
                  />
                </div>
                <div class="tw-w-full">
                  <WdsSelect
                    v-model="selectedDocumentType"
                    :list="identityDocumentTypeOptions"
                    :label="$t('Document type')"
                    :placeholder="$t('Select a type')"
                    data-test="managed-worker-identity-document-type"
                  />
                </div>
              </WdsStack>

              <WdsStack direction-row gap-sm class="managed-profile-form-row">
                <div class="tw-w-full">
                  <WdsInput
                    v-model="identityForm.documentNumber"
                    :label="$t('Document number')"
                    :placeholder="$t('Document number')"
                    data-test="managed-worker-identity-document-number"
                  />
                </div>
                <div class="tw-w-full">
                  <WdsInput
                    v-model="identityForm.documentExpiresAt"
                    type="date"
                    :label="$t('Document expires at')"
                    :min="tomorrowIso"
                    data-test="managed-worker-identity-document-expires-at"
                  />
                </div>
              </WdsStack>
            </WdsStack>

            <WdsStack direction-row justify-end>
              <WdsButton
                primary
                :loading="isSaving"
                data-test="managed-worker-save-profile"
                @click="saveProfile"
              >
                {{ $t('Save profile') }}
              </WdsButton>
            </WdsStack>

            <RightToWorkDocumentsFields
              :worker-global-id="workerId"
              :selected-nationality="selectedNationality?.value"
            />
          </WdsStack>
        </WdsTab>

        <WdsTab :title="$t('Client requirements')" :tab-key="MANAGED_PROFILE_TAB.CLIENTS">
          <WdsStack v-if="clientsWithRequirements.length === 0" direction-column gap-sm class="tw-mt-4">
            <WdsText secondary>
              {{ $t('No client onboarding fields to complete for this worker.') }}
            </WdsText>
          </WdsStack>
          <WdsStack v-else direction-column gap="4" class="tw-mt-4">
            <ClientCard
              v-for="client in clientsWithRequirements"
              :key="client.id"
              :client="{ id: client.id, status: client.status, company: client.company }"
            >
              <template #actions>
                <WdsLabel
                  v-if="client.hasRequiredCustomFields && client.requiredCustomFieldsComplete"
                  subtle
                  success
                >
                  {{ $t('Complete') }}
                </WdsLabel>
                <WdsLabel v-else-if="client.hasRequiredCustomFields" subtle warning>
                  {{ $t('Pending') }}
                </WdsLabel>
                <WdsText v-else secondary sm>
                  {{ $t('No required fields') }}
                </WdsText>
                <WdsButton
                  secondary
                  class="tw-items-center"
                  data-test="managed-worker-open-client-modal"
                  @click="openClient(client)"
                >
                  {{ $t('Edit') }}
                  <WdsIcon name="chevron-right" :size="16" class="tw-ml-1" />
                </WdsButton>
              </template>
            </ClientCard>
          </WdsStack>
        </WdsTab>

        <!--
          Worker-level compliance you complete on behalf of this worker. Today
          this is the NL BSN and the per-hire supplier employment confirmation;
          add further worker-level sections here as they come online.
        -->
        <WdsTab :title="$t('Compliance')" :tab-key="MANAGED_PROFILE_TAB.COMPLIANCE">
          <WdsStack direction-column gap-md class="tw-mt-4">
            <WdsStack
              v-if="bsnApplicable"
              direction-column
              gap-sm
              class="tw-border tw-border-gray-200 tw-rounded-md tw-bg-white tw-p-4"
            >
              <WdsStack direction-row justify-space-between align-center gap-sm>
                <WdsStack direction-column gap-xs>
                  <WdsText primary>{{ $t('BSN (Burgerservicenummer)') }}</WdsText>
                  <WdsText secondary sm>
                    {{
                      $t(
                        'Dutch tax and social security number required for workers in the Netherlands.',
                      )
                    }}
                  </WdsText>
                </WdsStack>
                <WdsLabel v-if="bsnCompleteState" subtle success xs>
                  {{ $t('Complete') }}
                </WdsLabel>
                <WdsLabel v-else subtle warning xs>
                  {{ $t('Pending') }}
                </WdsLabel>
              </WdsStack>
              <WdsStack direction-row gap-sm align-end class="managed-profile-form-row">
                <div class="tw-w-full">
                  <WdsInput
                    v-model="bsnInput"
                    :label="$t('BSN')"
                    :placeholder="$t('9-digit BSN')"
                    :error="bsnError"
                    data-test="managed-worker-bsn-input"
                  />
                </div>
                <WdsButton
                  primary
                  :loading="bsnSaving"
                  data-test="managed-worker-save-bsn"
                  @click="saveBsn"
                >
                  {{ $t('Save') }}
                </WdsButton>
              </WdsStack>
            </WdsStack>

            <WdsDivider v-if="bsnApplicable" />

            <WdsText secondary>
              {{
                $t(
                  'Per-hire supplier employment confirmation you need to complete on behalf of this worker.',
                )
              }}
            </WdsText>

            <WdsAlert v-if="applicableHires.length === 0" info icon>
              {{ $t('No supplier employment confirmation pending for this worker.') }}
            </WdsAlert>

            <WdsStack v-else direction-column gap="3">
              <WdsStack
                v-for="hire in applicableHires"
                :key="hire.id"
                class="tw-border tw-border-gray-200 tw-rounded-md tw-bg-white tw-p-4"
                direction-row
                justify-space-between
                align-center
                gap-sm
              >
                <WdsStack direction-column gap-xs>
                  <WdsText primary>{{ hire.complianceTitle }}</WdsText>
                  <WdsText sm>
                    <template v-if="hire.endClientName">
                      {{ hire.endClientName }}
                      <WdsText secondary sm>
                        {{ $t('via') }} {{ hire.mspName }}
                      </WdsText>
                    </template>
                    <template v-else>
                      {{ hire.mspName ?? $t('Unknown company') }}
                    </template>
                  </WdsText>
                  <WdsText secondary sm>
                    <template v-if="hire.jobName">{{ hire.jobName }} · </template>
                    {{ $t('Hire') }} #{{ hire.number }}
                  </WdsText>
                </WdsStack>

                <WdsStack direction-row align-center gap-sm>
                  <WdsLabel v-if="hire.completed" subtle success xs>
                    {{ $t('Complete') }}
                  </WdsLabel>
                  <WdsLabel v-else subtle warning xs>
                    {{ $t('Pending') }}
                  </WdsLabel>
                  <WdsButton
                    secondary
                    sm
                    data-test="managed-worker-open-supplier-confirmation"
                    @click="openSupplierConfirmation(hire.id)"
                  >
                    {{ hire.completed ? $t('View') : $t('Complete') }}
                    <WdsIcon name="chevron-right" :size="14" class="tw-ml-1" />
                  </WdsButton>
                </WdsStack>
              </WdsStack>
            </WdsStack>
          </WdsStack>
        </WdsTab>
      </WdsTabs>
    </div>
  </div>

  <WorkerProfileModal
    v-if="selectedClient"
    :show="showProfileModal"
    :client-id="selectedClient.id"
    :company-name="selectedClient.company.name"
    :company-id="selectedClient.company.id"
    :documents="[]"
    :custom-field-values="selectedClient.customFieldValues"
    :subject-account="workerId"
    @close="closeClient"
    @saved="handleSaved"
  />

  <NlSupplierEmploymentConfirmationModal
    v-if="supplierConfirmationHireId"
    :hire-id="supplierConfirmationHireId"
    :open="supplierConfirmationOpen"
    :prefilled-answers="supplierConfirmationAnswers"
    @update:open="onSupplierConfirmationToggle"
  />
</template>

<script setup lang="ts">
import axios from 'axios'
import { computed, reactive, ref, toRef, watch, type Ref } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsButtonGhost,
  WdsDivider,
  WdsHeading,
  WdsIcon,
  WdsInput,
  WdsLabel,
  WdsSelect,
  WdsSpinner,
  WdsStack,
  WdsTab,
  WdsTabs,
  WdsText,
} from '@worksome/wds-components'
import { useMutation, useQuery } from '@vue/apollo-composable'
import ClientCard from '@/components/Clients/ClientCard.vue'
import PageHeader from '@/components/PageHeader.vue'
import WorkerProfileModal from '@/components/WorkerClients/WorkerProfileModal.vue'
import NlSupplierEmploymentConfirmationModal from '@/components/Hire/HirePageCompliance/modals/NlSupplierEmploymentConfirmationModal.vue'
import RightToWorkDocumentsFields from '@/components/Identification/RightToWorkDocumentsFields.vue'
import {
  ManagedWorkerProfileQuery,
  UpdateManagedWorkerIdentificationMutation,
  UpdateManagedWorkerProfileMutation,
} from '@/components/Contacts/RecruiterFreelancer/graphql/queries'
import { IdentificationDocumentType } from '@/gql/graphql'
import { identificationDocumentTypeOptions } from '@/utils/identificationDocumentType'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useToast } from '@/composables/useToast'
import { useLocation } from '@/composables/useLocation'
import { useSelectOption } from '@/composables/useSelectOption'

const props = defineProps<{
  workerId: string
  bsn?: string | null
  bsnApplicable?: boolean
  bsnComplete?: boolean
  identificationApplicable?: boolean
  identificationRequiredBy?: string[]
}>()

const MANAGED_PROFILE_TAB = {
  PROFILE: 'profile',
  CLIENTS: 'clients',
  COMPLIANCE: 'compliance',
} as const

type ManagedProfileTab = (typeof MANAGED_PROFILE_TAB)[keyof typeof MANAGED_PROFILE_TAB]

const url = new URL(window.location.href)
const initialTabFromUrl = (Object.values(MANAGED_PROFILE_TAB) as string[])
  .find((t) => t === url.searchParams.get('tab')) as ManagedProfileTab | undefined
const activeTab = ref<ManagedProfileTab>(initialTabFromUrl ?? MANAGED_PROFILE_TAB.PROFILE)
const changeTab = (tab: ManagedProfileTab) => {
  url.searchParams.set('tab', tab)
  window.history.pushState({}, '', url.toString())
  activeTab.value = tab
}

const toast = useToast()
const {
  getCountries,
  getStates,
  setSelectedCountry,
  setSelectedState,
  postCodeLabel,
  postCodePlaceholder,
  stateLabel,
  statePlaceholder,
  countryHasStates,
} = useLocation()

const { result, loading, refetch } = useQuery(
  ManagedWorkerProfileQuery,
  { id: props.workerId },
  { clientId: 'publicApi' },
)

const worker = computed(() => result.value?.worker ?? null)
const viewerCanEdit = computed(() => worker.value?.viewerCanEdit ?? false)
const clients = computed(() => worker.value?.clients ?? [])

type ClientFromQuery = NonNullable<NonNullable<typeof result.value>['worker']>['clients'][number]

// Documents are never agency-actionable here, so actionable means worker-editable fields.
const clientsWithRequirements = computed(() =>
  clients.value.filter(
    (client) => client.requiresOnboarding && client.hasWorkerEditableCustomFields,
  ),
)

const form = reactive({
  firstName: '',
  middleName: '',
  lastName: '',
  country: '',
  state: '',
  address: {
    address: '',
    postCode: '',
    city: '',
  },
})

const selectedCountry = useSelectOption(toRef(form, 'country'), getCountries, (option) => {
  setSelectedCountry(option?.value ?? '')
  if (!option) form.state = ''
})

const selectedState = useSelectOption(toRef(form, 'state'), getStates, (option) => {
  setSelectedState(option?.value ?? '')
})

const identityForm = reactive({
  dateOfBirth: '',
  nationality: '',
  documentType: '' as IdentificationDocumentType | '',
  documentNumber: '',
  documentExpiresAt: '',
})

const tomorrowIso = computed(() => {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return d.toISOString().split('T')[0]
})

const yesterdayIso = computed(() => {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toISOString().split('T')[0]
})

const selectedNationality = useSelectOption(toRef(identityForm, 'nationality'), getCountries)

const identityDocumentTypeOptions = computed(() => identificationDocumentTypeOptions())
const selectedDocumentType = useSelectOption(
  toRef(identityForm, 'documentType') as Ref<IdentificationDocumentType | ''>,
  identityDocumentTypeOptions,
)

watch(worker, (next) => {
  if (!next) return
  form.firstName = next.firstName ?? ''
  form.middleName = next.middleName ?? ''
  form.lastName = next.lastName ?? ''
  form.address.address = next.address?.address ?? ''
  form.address.postCode = next.address?.postCode ?? ''
  form.address.city = next.address?.city ?? ''
  form.state = next.address?.state ?? ''
  form.country = next.address?.country?.isoCode ?? ''
  setSelectedCountry(form.country)
  setSelectedState(form.state)

  const id = next.identification
  identityForm.dateOfBirth = id?.dateOfBirth ?? ''
  identityForm.nationality = id?.nationality ?? ''
  identityForm.documentType = (id?.documentType ?? '') as IdentificationDocumentType | ''
  identityForm.documentNumber = id?.documentNumber ?? ''
  identityForm.documentExpiresAt = id?.documentExpiresAt ?? ''
}, { immediate: true })

const { mutate: updateWorker, loading: profileSaving } = useMutation(UpdateManagedWorkerProfileMutation, {
  clientId: 'publicApi',
})

// The right-to-work requirement follows the saved nationality, so the child query refetches with the mutation.
const { mutate: updateWorkerIdentification, loading: identitySaving } = useMutation(
  UpdateManagedWorkerIdentificationMutation,
  {
    clientId: 'publicApi',
    refetchQueries: ['workerRightToWork'],
    awaitRefetchQueries: true,
  },
)

const isIdentityFormFilled = computed(() => Boolean(
  identityForm.dateOfBirth
    && identityForm.nationality
    && identityForm.documentType
    && identityForm.documentNumber
    && identityForm.documentExpiresAt,
))

// identityForm is pre-filled from the query, so "dirty" means "has values", not "user typed".
const isIdentitySectionDirty = computed(() => Boolean(
  identityForm.dateOfBirth
    || identityForm.nationality
    || identityForm.documentType
    || identityForm.documentNumber
    || identityForm.documentExpiresAt,
))

const identificationRequiredByLabel = computed(() =>
  (props.identificationRequiredBy ?? []).join(', '),
)

const isSaving = computed(() => profileSaving.value || identitySaving.value)


const readableSaveError = (error: unknown): string => {
  const graphQLError = (error as { graphQLErrors?: Array<Record<string, any>> })?.graphQLErrors?.[0]
  const validation = graphQLError?.extensions?.validation

  if (validation && typeof validation === 'object') {
    return Object.values(validation as Record<string, string[]>).flat().join(' ')
  }

  return (graphQLError?.message as string | undefined) ?? $t('Could not save profile')
}

const saveProfile = async () => {
  // Validate before any mutation runs, so a refused identity save can't half-persist the profile.
  if (props.identificationApplicable && isIdentitySectionDirty.value && !isIdentityFormFilled.value) {
    toast.error($t('Please complete all identification fields, or clear them all.'))
    return
  }

  try {
    await updateWorker({
      input: {
        id: props.workerId,
        firstName: form.firstName,
        middleName: form.middleName,
        lastName: form.lastName,
        address: {
          address: form.address.address || null,
          postCode: form.address.postCode || null,
          city: form.address.city || null,
          state: form.state || null,
          country: form.country || null,
        },
      },
    })

    if (props.identificationApplicable && isIdentitySectionDirty.value) {
      await updateWorkerIdentification({
        input: {
          workerId: props.workerId,
          dateOfBirth: identityForm.dateOfBirth,
          nationality: identityForm.nationality,
          documentType: identityForm.documentType as IdentificationDocumentType,
          documentNumber: identityForm.documentNumber,
          documentExpiresAt: identityForm.documentExpiresAt,
        },
      })
    }

    toast.success($t('Profile saved'))
    await refetch()
  } catch (e) {
    // The causes here are all agency-fixable, so a generic message would hide which field is at fault.
    toast.error(readableSaveError(e))
  }
}


const showProfileModal = ref(false)
const selectedClientId = ref<string | null>(null)

const selectedClient = computed(() =>
  clients.value.find((c) => c.id === selectedClientId.value) ?? null,
)

const openClient = (client: ClientFromQuery) => {
  selectedClientId.value = client.id
  showProfileModal.value = true
}

const closeClient = () => {
  selectedClientId.value = null
  showProfileModal.value = false
}

const handleSaved = async () => {
  await refetch()
  closeClient()
}

// Compliance tab: per-hire NL supplier employment confirmation list.
const hires = computed(() => worker.value?.hiresWithAttribution ?? [])

const applicableHires = computed(() =>
  hires.value
    .map((hire) => {
      const compliance = hire.compliances?.[0]
      // Supplier-mirror hire: hire.company is the SA/MSP, endClient is the end client.
      const endClientName = hire.endClient?.name ?? null
      const mspName = hire.company?.name ?? null
      return {
        id: hire.id,
        number: hire.number,
        endClientName,
        mspName,
        jobName: hire.job?.name ?? null,
        applicable: compliance?.applicable ?? false,
        completed: compliance?.completed ?? false,
        complianceTitle: compliance?.title ?? $t('Supplier employment confirmation'),
      }
    })
    .filter((row) => row.applicable),
)

const supplierConfirmationHireId = ref<string | null>(null)
const supplierConfirmationOpen = ref(false)

// Pre-built answer fields for the open hire (avoids modal opening empty).
const supplierConfirmationAnswers = computed(() => {
  if (supplierConfirmationHireId.value === null) return null
  const hire = hires.value.find((h) => h.id === supplierConfirmationHireId.value)
  const fields = hire?.compliances?.[0]?.data ?? []
  return fields.filter((field) => field.__typename === 'ComplianceDataField')
})

const openSupplierConfirmation = (hireId: string) => {
  supplierConfirmationHireId.value = hireId
  supplierConfirmationOpen.value = true
}

const onSupplierConfirmationToggle = async (open: boolean) => {
  supplierConfirmationOpen.value = open
  if (!open) {
    supplierConfirmationHireId.value = null
    await refetch()
  }
}

// Worker-level BSN. The number is sensitive, so it is read and written through
// a controller route rather than the public GraphQL API; the controller passes
// the current value and status in as props.
const bsnInput = ref(props.bsn ?? '')
const bsnCompleteState = ref(props.bsnComplete ?? false)
const bsnSaving = ref(false)
const bsnError = ref<string | undefined>(undefined)

// Clear the field error as soon as the recruiter starts correcting the number.
watch(bsnInput, () => {
  bsnError.value = undefined
})

const saveBsn = async () => {
  bsnSaving.value = true
  bsnError.value = undefined
  try {
    const { data } = await axios.put(
      laravelNamedRoute('recruiter-candidates.compliances.update', {
        worker: props.workerId,
        compliance: 'NL_BSN',
      }),
      { data: { nl_bsn: bsnInput.value } },
    )
    bsnCompleteState.value = data.completed ?? false
    toast.success($t('BSN saved'))
  } catch (e) {
    // A rejected number comes back as a validation error (422) with the exact
    // reason for the field, so show that under the input. Anything else is an
    // unexpected failure, so fall back to a generic message.
    const errors =
      axios.isAxiosError(e) && e.response?.status === 422
        ? (e.response.data?.errors as Record<string, string[]> | undefined)
        : undefined
    const validationMessage = errors ? Object.values(errors)[0]?.[0] : undefined

    if (validationMessage) {
      bsnError.value = validationMessage
    } else {
      toast.error($t('Could not save BSN'))
    }
  } finally {
    bsnSaving.value = false
  }
}
</script>

<style scoped>
/* Keep row stacks in this form on one line. */
.managed-profile-form-row :deep(.wds-stack-direction-row),
.managed-profile-form-row.wds-stack-direction-row {
  display: inline-flex;
}
</style>
